import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EVENT_WORLD_SCOPES,
  EVENT_WORLD_SCOPE_LABELS,
  MEMBER_TYPES,
  MEMBER_TYPE_LABELS,
  isGuestMemberType,
  isInactiveMemberType,
  type WineCategoryView,
} from '@gwc/shared';
import {
  categoriesForSave,
  eventSetupFromEvent,
  rsvpApi,
  type EventSetupValue,
} from '../api/rsvp';
import { tastingsApi } from '../api/tastings';
import type { EventRow } from '../api/models';
import { ApiError } from '../lib/api';
import { WineCategoriesEditor } from './WineCategoriesEditor';

/** Inactive types (past members, in memoriam) are never offered. */
const INVITABLE_TYPES = MEMBER_TYPES.filter((t) => !isInactiveMemberType(t));

/** The cutoff the club uses when a tasting starts taking payment. */
const DEFAULT_CUTOFF_DAYS = 3;

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-ink-800 p-4">
      <div>
        <h3 className="text-sm uppercase tracking-wide text-gold-400">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-cream-300/50">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

/** Problems that would make the server refuse the setup, in plain words. */
function setupProblems(v: EventSetupValue): string | null {
  if (v.requiresPayment && !(v.price != null && v.price > 0))
    return 'Set a price to take payment on RSVP.';
  if (v.invitedMemberTypes.length === 0) return 'Invite at least one member type.';
  return null;
}

/**
 * The RSVP side of a tasting's setup: Old/New World, wine categories, the
 * rules, who's invited, payment, the dues gate and the refund cutoff. Used by
 * the create form (categories go in the create call) and by
 * `EventSetupEditor` on the setup page (categories saved separately).
 */
export function EventSetupFields({
  value,
  onChange,
  categoryUsage,
  disabled,
}: {
  value: EventSetupValue;
  onChange: (v: EventSetupValue) => void;
  /** Saved categories with their counts, when editing a tasting. */
  categoryUsage?: WineCategoryView[];
  disabled?: boolean;
}) {
  const set = (patch: Partial<EventSetupValue>) => onChange({ ...value, ...patch });
  const toggleType = (t: (typeof INVITABLE_TYPES)[number], on: boolean) => {
    const next = on
      ? [...value.invitedMemberTypes, t]
      : value.invitedMemberTypes.filter((x) => x !== t);
    if (next.length) set({ invitedMemberTypes: next });
  };

  return (
    <div className="space-y-4">
      <Section title="Which wines" hint="Old World wines are entered by commune, with no varietal.">
        <div className="grid gap-2 sm:grid-cols-3" role="radiogroup">
          {EVENT_WORLD_SCOPES.map((s) => (
            <label
              key={s}
              className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                value.worldScope === s
                  ? 'border-gold-400 bg-gold-600/10 text-gold-200'
                  : 'border-ink-800 text-cream-200'
              }`}
            >
              <input
                type="radio"
                name="world-scope"
                className="accent-gold-500"
                checked={value.worldScope === s}
                disabled={disabled}
                onChange={() => set({ worldScope: s })}
              />
              {EVENT_WORLD_SCOPE_LABELS[s]}
            </label>
          ))}
        </div>
      </Section>

      <Section
        title="Wine categories"
        hint="Optional. Members pick one when they RSVP; a full category is closed to new picks."
      >
        <WineCategoriesEditor
          value={value.wineCategories}
          onChange={(wineCategories) => set({ wineCategories })}
          usage={categoryUsage}
          disabled={disabled}
        />
      </Section>

      <Section title="Rules" hint="Shown to members on the RSVP, e.g. price range or what to bring.">
        <textarea
          className="field min-h-[80px]"
          maxLength={5000}
          placeholder="Bring a Pinot Noir from the Santa Lucia Highlands, $40–$80."
          value={value.rules}
          disabled={disabled}
          onChange={(e) => set({ rules: e.target.value })}
        />
      </Section>

      <Section
        title="Who's invited"
        hint="Only these member types can RSVP themselves. You can open it to more types later."
      >
        <div className="grid gap-2 sm:grid-cols-2">
          {INVITABLE_TYPES.map((t) => {
            const checked = value.invitedMemberTypes.includes(t);
            return (
              <label key={t} className="flex items-center gap-2 text-sm text-cream-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-gold-500"
                  checked={checked}
                  disabled={disabled || (checked && value.invitedMemberTypes.length === 1)}
                  onChange={(e) => toggleType(t, e.target.checked)}
                />
                {MEMBER_TYPE_LABELS[t]}
                {isGuestMemberType(t) && (
                  <span className="text-xs text-cream-300/50">(guests)</span>
                )}
              </label>
            );
          })}
        </div>
      </Section>

      <Section title="Payment">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
              Price per seat
            </span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step={0.01}
              className="field"
              placeholder="No charge"
              value={value.price ?? ''}
              disabled={disabled}
              onChange={(e) => set({ price: e.target.value ? Number(e.target.value) : null })}
            />
          </label>
          {value.requiresPayment && (
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
                No refund within (days)
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={60}
                className="field"
                placeholder="Always refundable"
                value={value.cancellationCutoffDays ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  set({
                    cancellationCutoffDays: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
            </label>
          )}
        </div>
        <label className="flex items-start gap-3 text-sm text-cream-200">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-gold-500"
            checked={value.requiresPayment}
            disabled={disabled}
            onChange={(e) =>
              set({
                requiresPayment: e.target.checked,
                cancellationCutoffDays:
                  e.target.checked && value.cancellationCutoffDays == null
                    ? DEFAULT_CUTOFF_DAYS
                    : value.cancellationCutoffDays,
              })
            }
          />
          <span>
            Pay to confirm the seat
            <span className="block text-xs text-cream-300/50">
              Members pay by card when they RSVP. The price includes the card fee. Admins can
              still mark someone paid or waived.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm text-cream-200">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-gold-500"
            checked={value.requiresDuesPaid}
            disabled={disabled}
            onChange={(e) => set({ requiresDuesPaid: e.target.checked })}
          />
          <span>
            Annual dues must be paid
            <span className="block text-xs text-cream-300/50">
              Members with dues outstanding are asked to settle them before they can RSVP.
            </span>
          </span>
        </label>
        {value.requiresPayment && !(value.price != null && value.price > 0) && (
          <p className="text-xs text-wine-300">Set a price to take payment on RSVP.</p>
        )}
      </Section>
    </div>
  );
}

/**
 * The setup page's editor for an existing tasting: the fields above, saved as
 * an event PATCH plus — when the categories changed — a PUT of the categories.
 */
export function EventSetupEditor({ event }: { event: EventRow }) {
  const qc = useQueryClient();
  const categoriesQuery = useQuery({
    queryKey: ['wine-categories', event.id],
    queryFn: () => rsvpApi.categories(event.id),
    retry: false,
  });
  const usage = categoriesQuery.data;
  const loaded = !categoriesQuery.isLoading;

  const [value, setValue] = useState<EventSetupValue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed once the categories are in (or failed to load).
  useEffect(() => {
    if (loaded && value === null) setValue(eventSetupFromEvent(event, usage ?? []));
  }, [loaded, value, event, usage]);

  const save = useMutation({
    mutationFn: async (v: EventSetupValue) => {
      await tastingsApi.update(event.id, {
        worldScope: v.worldScope,
        rules: v.rules.trim() || null,
        invitedMemberTypes: v.invitedMemberTypes,
        requiresPayment: v.requiresPayment,
        requiresDuesPaid: v.requiresDuesPaid,
        cancellationCutoffDays: v.cancellationCutoffDays,
        price: v.price,
      });
      if (!usage) return [];
      const before = categoriesForSave(eventSetupFromEvent(event, usage).wineCategories);
      const after = categoriesForSave(v.wineCategories);
      if (JSON.stringify(after) === JSON.stringify(before)) return usage;
      return rsvpApi.putCategories(event.id, after);
    },
    onSuccess: (fresh) => {
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (usage) {
        qc.setQueryData(['wine-categories', event.id], fresh);
        // New rows now have ids; take them so the next save renames, not re-creates.
        setValue((v) =>
          v ? { ...v, wineCategories: eventSetupFromEvent(event, fresh).wineCategories } : v,
        );
      }
      void qc.invalidateQueries({ queryKey: ['event', event.id] });
      void qc.invalidateQueries({ queryKey: ['rsvp', event.id] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save'),
  });

  return (
    <form
      className="card mb-6 space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!value) return;
        const problem = setupProblems(value);
        if (problem) {
          setError(problem);
          return;
        }
        save.mutate(value);
      }}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg text-gold-200">RSVP &amp; wine setup</h2>
        {saved && <span className="text-xs text-gold-200">Saved ✓</span>}
      </div>
      {categoriesQuery.isError && (
        <p className="text-xs text-warning">
          The wine categories couldn&apos;t be loaded, so saving here won&apos;t change them.
        </p>
      )}
      {value ? (
        <EventSetupFields
          value={value}
          onChange={setValue}
          categoryUsage={usage}
          disabled={save.isPending}
        />
      ) : (
        <p className="text-sm text-cream-300/60">Loading…</p>
      )}
      {error && <p className="text-sm text-wine-300">{error}</p>}
      <button type="submit" className="btn-primary" disabled={!value || save.isPending}>
        {save.isPending ? 'Saving…' : 'Save setup'}
      </button>
    </form>
  );
}
